import { Link } from "react-router-dom";
import { Leaf, Sprout, BadgeCheck, Drumstick } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Motion";
import PageHeader from "../components/layout/PageHeader";
import { assets, business } from "../data/site";
import SEO from "../components/SEO";

const chapters = [
  {
    n: "01",
    title: "It Began in 1995",
    body: "We opened our doors on Merivale Road with one simple goal — to share the warm, home-cooked flavours of India with the city of Ottawa. Nearly three decades later, that promise still guides everything we do.",
    image: assets.gallery[0],
  },
  {
    n: "02",
    title: "A Family Kitchen",
    body: "Every curry, every naan and every samosa is prepared the traditional way, using whole spices we grind in-house and recipes passed down through generations. Good food takes time, and we never rush it.",
    image: assets.gallery[4],
  },
  {
    n: "03",
    title: "A Table For Everyone",
    body: "Ottawa is a diverse city, and our menu reflects it. We proudly serve Vegetarian, Vegan, 100% Halal and Non-Vegetarian dishes — so no one is ever left out of the meal.",
    image: assets.gallery[6],
  },
  {
    n: "04",
    title: "Trusted By Families",
    body: "We've watched children who came in with their parents grow up and bring their own families. That trust, earned one plate at a time, is the greatest reward of all.",
    image: assets.gallery[9],
  },
];

const badges = [
  { icon: Leaf, label: "Vegetarian" },
  { icon: Sprout, label: "Vegan" },
  { icon: BadgeCheck, label: "100% Halal" },
  { icon: Drumstick, label: "Non-Veg" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        route="about"
        fallbackTitle="Our Story | Shaan Curry House"
        fallbackDescription="Family-owned Indian kitchen serving Ottawa since 1995."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Our Story", url: "/about" },
        ]}
      />
      <PageHeader
        eyebrow="Our Story"
        title="Nearly 30 years of authentic Indian cooking."
      />

      {/* CHAPTERS */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 space-y-20 md:space-y-28">
        {chapters.map((c, i) => (
          <div key={c.n} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <Reveal>
              <span className="font-display font-bold text-[#ffd100]/30 text-7xl md:text-8xl leading-none">{c.n}</span>
              <h2 className="font-display font-semibold text-3xl md:text-4xl text-white mt-2 tracking-tight">{c.title}</h2>
              <p className="mt-5 text-neutral-300 text-lg leading-relaxed">{c.body}</p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="relative">
                <img src={c.image} alt={c.title} loading="lazy" className="rounded-2xl w-full h-72 md:h-96 object-cover" />
                <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10" />
              </div>
            </Reveal>
          </div>
        ))}
      </section>

      {/* VALUES / BADGES */}
      <section className="bg-[#ffd100]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-14">
          <StaggerGroup className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {badges.map(({ icon: Icon, label }) => (
              <StaggerItem key={label} className="flex items-center gap-3 justify-center">
                <Icon size={30} strokeWidth={2.4} className="text-[#0a0a0a]" />
                <span className="font-display font-semibold text-[#0a0a0a] text-lg md:text-xl">{label}</span>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* GALLERY */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal className="mb-10">
          <h2 className="font-display font-semibold text-4xl md:text-5xl text-white tracking-tight">From Our Kitchen</h2>
        </Reveal>
        <StaggerGroup className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {assets.gallery.slice(0, 8).map((g, i) => (
            <StaggerItem key={g} stagger={0.08}>
              <img src={g} alt={`Shaan Curry House gallery ${i + 1}`} loading="lazy"
                className="rounded-xl w-full h-44 md:h-56 object-cover hover:opacity-90 transition-opacity" />
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* CTA */}
      <section className="bg-[#141414] border-t border-white/10">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-20 text-center">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-5xl text-white tracking-tight">Come taste the difference.</h2>
            <p className="mt-4 text-neutral-400 text-lg">Visit us at {business.address}.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/menu" className="inline-flex items-center justify-center bg-[#ffd100] text-[#0a0a0a] font-bold text-lg px-8 py-4 rounded-full hover:bg-[#e5bc00] transition-colors">View Menu</Link>
              <Link to="/contact" className="inline-flex items-center justify-center border-2 border-white/20 text-white font-bold text-lg px-8 py-4 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors">Contact Us</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
